import { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";

import { ARTIST, PORTRAIT_URL, MARQUEE_PHRASES } from "@/lib/constants";
import { easeOutExpo, revealLine } from "@/lib/motion";
import { scrollToId } from "@/components/site/SmoothScroll";
import { HeroAtmosphere } from "@/components/site/HeroAtmosphere";
import { CredibilityBar } from "@/components/site/CredibilityBar";

const HERO_TITLE_LINES = ["IRWAN", "CRAW"];

const Line = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span
      variants={revealLine}
      initial="hidden"
      animate="visible"
      transition={{ ...revealLine.visible.transition, delay }}
      className="block"
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const heroRef = useRef(null);

  // Parallax portrait (very subtle)
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });
  const tx = useTransform(sx, [-1, 1], [-14, 14]);
  const ty = useTransform(sy, [-1, 1], [-10, 10]);
  const tiltX = useTransform(sy, [-1, 1], [2.5, -2.5]);
  const tiltY = useTransform(sx, [-1, 1], [-2.5, 2.5]);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      mx.set(x * 2);
      my.set(y * 2);
    };
    el.addEventListener("mousemove", onMove);
    return () => el.removeEventListener("mousemove", onMove);
  }, [mx, my]);

  return (
    <section
      id="hero"
      ref={heroRef}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-bg grain"
    >
      {/* Cinematic atmosphere layers (light beams, particles, haze) */}
      <HeroAtmosphere />

      {/* Vertical hairline column marker */}
      <div className="pointer-events-none absolute left-6 md:left-10 top-0 bottom-0 hidden md:block">
        <div className="h-full w-px bg-gradient-to-b from-transparent via-[color:var(--line-strong)] to-transparent" />
      </div>

      {/* Corner meta */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="pointer-events-none absolute top-24 left-14 hidden md:flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-[0.32em] text-muted"
      >
        <span>N° 001 — Portrait</span>
        <span className="text-gold">MMXXV</span>
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="pointer-events-none absolute top-24 right-10 hidden md:flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.32em] text-muted"
      >
        <span className="h-1 w-1 rounded-full bg-[color:var(--gold)]" />
        <span>Available Worldwide</span>
      </motion.div>

      <div className="relative z-10 mx-auto grid min-h-[100svh] max-w-[1500px] grid-cols-1 lg:grid-cols-12 gap-8 px-6 pt-32 pb-16 md:px-10 md:pt-36 md:pb-24">
        {/* LEFT — content */}
        <div className="lg:col-span-7 flex flex-col justify-between">
          <div className="max-w-3xl">
            {/* Overline */}
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4, ease: easeOutExpo }}
              data-testid="hero-overline"
              className="mb-8 inline-flex items-center gap-3 text-[10px] md:text-[11px] font-semibold tracking-[0.32em] uppercase text-gold"
            >
              <span className="h-px w-10 bg-[color:var(--gold)]/70" />
              {ARTIST.tagline}
            </motion.p>

            {/* Kinetic masked title */}
            <h1
              data-testid="hero-title"
              className="font-display text-[16vw] leading-[0.86] md:text-[11vw] lg:text-[10.5rem] font-black uppercase tracking-tighter"
            >
              {HERO_TITLE_LINES.map((line, i) => (
                <Line key={line} delay={0.6 + i * 0.14}>
                  <span
                    className={
                      i === 1
                        ? "text-shine"
                        : "text-ink"
                    }
                  >
                    {line}
                  </span>
                </Line>
              ))}
            </h1>

            {/* Editorial signature line */}
            <div className="mt-6 max-w-xl overflow-hidden">
              <motion.p
                variants={revealLine}
                initial="hidden"
                animate="visible"
                transition={{ ...revealLine.visible.transition, delay: 1.1 }}
                data-testid="hero-story-short"
                className="font-editorial text-2xl md:text-3xl text-ink/90"
              >
                {ARTIST.storyShort}
              </motion.p>
            </div>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.4, ease: easeOutExpo }}
              data-testid="hero-story-long"
              className="mt-6 max-w-xl text-sm md:text-base text-muted leading-relaxed"
            >
              {ARTIST.storyLong}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.65, ease: easeOutExpo }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <a
                data-testid="hero-listen-cta"
                href={ARTIST.spotifyArtistUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="group relative overflow-hidden inline-flex items-center justify-center gap-3 rounded-full bg-[color:var(--gold)] px-7 py-4 text-[11px] font-bold uppercase tracking-[0.28em] text-[#0a0a0a] transition-all duration-500 hover:shadow-[0_0_45px_rgba(201,169,97,0.55)]"
              >
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/60 to-transparent transition-transform duration-1000 ease-out group-hover:translate-x-full"
                />
                <Play size={14} className="relative fill-current" />
                <span className="relative">Listen Now</span>
                <ArrowRight
                  size={14}
                  className="relative transition-transform duration-500 group-hover:translate-x-1.5"
                />
              </a>
              <button
                data-testid="hero-book-cta"
                onClick={() => scrollToId("book")}
                className="group relative overflow-hidden inline-flex items-center justify-center gap-3 rounded-full border border-[color:var(--gold)]/50 bg-transparent px-7 py-4 text-[11px] font-bold uppercase tracking-[0.28em] text-[color:var(--gold-hi)] transition-all duration-500 hover:border-[color:var(--gold)] hover:text-[#0a0a0a]"
              >
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 -translate-x-full bg-[color:var(--gold)] transition-transform duration-500 ease-out group-hover:translate-x-0"
                />
                <span className="relative">Book Irwan Craw</span>
                <ArrowRight
                  size={14}
                  className="relative transition-transform duration-500 group-hover:translate-x-1.5"
                />
              </button>
            </motion.div>
          </div>

          {/* Credibility Bar — labels + radio residency */}
          <CredibilityBar />

          {/* Bottom meta row */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.9, duration: 1 }}
            className="mt-16 hidden md:flex items-center justify-between border-t border-[color:var(--line)] pt-6 text-[10px] uppercase tracking-[0.32em] text-muted"
          >
            <span>DJ · Producer · Independent</span>
            <button
              onClick={() => scrollToId("releases")}
              className="group inline-flex items-center gap-2 text-ink/80 hover:text-[color:var(--gold-hi)] transition"
            >
              Discover the catalog
              <ArrowRight
                size={12}
                className="transition-transform group-hover:translate-x-1"
              />
            </button>
          </motion.div>
        </div>

        {/* RIGHT — Portrait */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.4, delay: 0.5, ease: easeOutExpo }}
            style={{
              x: tx,
              y: ty,
              rotateX: tiltX,
              rotateY: tiltY,
              transformPerspective: 1200,
            }}
            className="relative mx-auto aspect-[4/5] w-full max-w-[520px] lg:sticky lg:top-28"
          >
            {/* Golden rim halo behind portrait */}
            <div
              aria-hidden
              className="pointer-events-none absolute -inset-8 rounded-[6px] opacity-70"
              style={{
                background:
                  "radial-gradient(ellipse 80% 60% at 50% 40%, rgba(229,204,148,0.28), transparent 70%)",
                filter: "blur(20px)",
              }}
            />
            {/* Frame */}
            <div className="absolute inset-0 rounded-[2px] border border-[color:var(--line-strong)]" />
            <div className="absolute -inset-4 rounded-[4px] border border-[color:var(--line)] pointer-events-none" />

            {/* Portrait */}
            <div className="relative h-full w-full overflow-hidden rounded-[2px]">
              <img
                src={PORTRAIT_URL}
                alt="Irwan Craw — DJ & Producer"
                className="h-full w-full object-cover"
                style={{
                  filter:
                    "contrast(1.14) brightness(0.92) saturate(1.08) sepia(0.14) hue-rotate(-4deg)",
                }}
                data-testid="hero-portrait"
              />

              {/* Warm rim light — right side */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "linear-gradient(275deg, rgba(229,204,148,0.32) 0%, rgba(201,169,97,0.10) 12%, transparent 30%)",
                  mixBlendMode: "screen",
                }}
              />
              {/* Sunset overlay */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "linear-gradient(140deg, rgba(229,204,148,0.22) 0%, rgba(201,169,97,0.08) 25%, rgba(0,0,0,0) 50%, rgba(20,15,8,0.55) 100%)",
                  mixBlendMode: "overlay",
                }}
              />
              {/* Studio wall darken */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(10,10,10,0.42) 0%, rgba(10,10,10,0) 22%, rgba(10,10,10,0) 60%, rgba(10,10,10,0.78) 100%)",
                }}
              />
              {/* Vignette */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "radial-gradient(ellipse 70% 55% at 50% 45%, transparent 30%, rgba(0,0,0,0.72) 100%)",
                }}
              />
              {/* Grain */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-[0.14] mix-blend-overlay"
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/></svg>\")",
                }}
              />
              {/* Signature caption */}
              <div className="absolute left-4 bottom-4 right-4 flex items-end justify-between">
                <div className="text-[9px] font-semibold uppercase tracking-[0.32em] text-ink/80">
                  <span className="text-gold">Portrait</span> · Studio · 2025
                </div>
                <span className="font-serif-signature text-lg text-[color:var(--gold-hi)]/90">
                  Irwan Craw
                </span>
              </div>
            </div>

            {/* Floating gold accents */}
            <div className="pointer-events-none absolute -right-3 -top-3 h-3 w-3 rounded-full bg-[color:var(--gold)] shadow-[0_0_18px_rgba(201,169,97,0.65)] float-soft" />
            <div className="pointer-events-none absolute -left-3 -bottom-3 h-1.5 w-1.5 rounded-full bg-[color:var(--gold-hi)] float-soft" />
          </motion.div>
        </div>
      </div>

      {/* Editorial marquee */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 border-t border-[color:var(--line)] bg-[#0a0a0a]/60 backdrop-blur-md">
        <div className="overflow-hidden py-4">
          <div className="marquee-track inline-flex whitespace-nowrap">
            {[...MARQUEE_PHRASES, ...MARQUEE_PHRASES, ...MARQUEE_PHRASES].map(
              (p, i) => (
                <span
                  key={`${p}-${i}`}
                  className="mx-8 inline-flex items-center gap-6 font-editorial text-xl md:text-2xl text-ink/60"
                >
                  {p}
                  <span className="h-1 w-1 rounded-full bg-[color:var(--gold)]" />
                </span>
              )
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
